import axios from 'axios';
import { BACKEND_BASEURL } from "@/config/config";
import MeterCodes from "@/lib/es-bill-shared-js/MeterCodes";

let configBackendBaseUrl = "";
let $log;

// used by app.use
export default function setup(app) {
  configBackendBaseUrl = BACKEND_BASEURL;
  $log = app.$log;
}

/**
 * Utility function to check if a date is valid
 * @param {Date} date - Date to check
 * @returns {boolean} True if date is valid
 * @private
 */
function _isDateValid(date) {
  try {
    return date instanceof Date && date.getTime() === date.getTime();
  } catch (e) {
    return false;
  }
}



/**
 * Reduce date range data by filtering within specified date range
 * Matches the logic from classic-tools.js
 * @param {Array} inputRange - Input date range array
 * @param {Date} dateStart - Start date
 * @param {Date} dateEnd - End date
 * @returns {Array} Filtered date range array
 * @private
 */
function _reduceDateRangeDataByDateRange(inputRange, dateStart, dateEnd) {
  let outputRange = [];
  if (dateStart > dateEnd || dateStart === null || dateEnd === null)
    return outputRange;

  // remove time
  dateStart.setHours(0, 0, 0, 0); // in local timezone
  dateEnd.setHours(0, 0, 0, 0); // in local timezone

  // use new data structure, delete unneeded entries, reduce array(s)
  Object.assign(outputRange, inputRange);
  let indexYear = 0;
  for (const yearEntry of outputRange) {
    if (yearEntry.year < dateStart.getFullYear() ||
        yearEntry.year > dateEnd.getFullYear()) {
      delete outputRange[indexYear];
      indexYear++;
      continue;
    }

    // year is ok, go one step deeper
    let indexMonth = 0
    for (const monthEntry of yearEntry.months) {
      let dateMonthBegin = new Date(yearEntry.year, monthEntry.month - 1, 1);
      let dateMonthEnd = new Date(yearEntry.year, monthEntry.month, 0);

      if (dateMonthEnd < dateStart ||
          dateMonthBegin > dateEnd) {
        delete yearEntry.months[indexMonth];
        indexMonth++;
        continue;
      }

      let indexDay = 0
      for (const dayEntry of monthEntry.days) {
        let day = new Date(yearEntry.year, monthEntry.month - 1, dayEntry.day);
        if (day < dateStart ||
            day > dateEnd) {
          delete monthEntry.days[indexDay];
        }
        indexDay++;
      }

      monthEntry.days = monthEntry.days.filter(value => value); // remove deleted
      indexMonth++
    }
    yearEntry.months = yearEntry.months.filter(value => value); // remove deleted
    indexYear++
  }
  outputRange = outputRange.filter(value => value); // remove deleted

  return outputRange;
}

/**
 * Convert date range structure to array of dates
 * Matches the logic from classic-tools.js
 * @param {Array} inputRange - Date range array
 * @returns {Array<Date>} Array of dates
 * @private
 */
function _convertDateRangeToDates(inputRange) {
  let output = [];

  for (const yearEntry of inputRange) {
    for (const monthEntry of yearEntry.months) {
      for (const dayEntry of monthEntry.days) {
        const dateEntry = new Date(yearEntry.year, monthEntry.month - 1, dayEntry.day);
        if (_isDateValid(dateEntry))
          output.push(dateEntry);
      }
    }
  }

  // sort by time to be sure there was no mess with any order
  output.sort((a, b) => a.getTime() - b.getTime());

  return output;
}

/**
 * Extract date ranges from array of dates - groups consecutive dates
 * Matches the logic from classic-tools.js
 * @param {Array<Date>} input - Array of dates
 * @returns {Array<Array<Date>>} Array of date range pairs [start, end]
 * @private
 */
function _extractDateRangesFromDateArray(input) {
  let ranges = [];

  let start;
  let last;
  for (const currentDate of input) {
    if (!start) {
      // no sequence open
      start = currentDate;
      continue;
    }

    // gap too long?
    if (!last && (currentDate.getTime() - start.getTime()) / (1000 * 60 * 60 * 24) > 1
        || last && (currentDate.getTime() - last.getTime()) / (1000 * 60 * 60 * 24) > 1) {
      // close old entry
      if (last)
        ranges.push([start, last])
      else
        ranges.push([start, start])

      // open new entry
      start = currentDate;
      last = null;
      continue;
    }

    // should be one day apart
    last = currentDate;
  }

  // close open
  if (last)
    ranges.push([start, last])
  else if (start)
    ranges.push([start, start])

  return ranges;
}

/**
 * Fetch community-wide date ranges for bill run analysis
 * @param {string} ecn - Community ECN
 * @returns {Promise<Array>} Array of year/month/day ranges
 */
export async function fetchCommunityDateRanges(ecn) {
  if (!ecn) {
    $log.error('No ECN provided. Cannot fetch community date ranges.');
    throw new Error('No ECN provided');
  }

  try {
    const response = await axios.get(`${configBackendBaseUrl}data/${ecn}/dateRange/all`);
    return response.data;
  } catch (error) {
    $log.error(`Error fetching community date ranges for ${ecn}:`, error);
    throw error;
  }
}

/**
 * Fetch member-specific date ranges for a metering point
 * @param {string} ecn - Community ECN
 * @param {string} meteringPoint - Metering point identifier
 * @param {string} meterCode - Meter code (e.g., 'producerProductionTotal', 'memberConsumptionTotal')
 * @returns {Promise<Array>} Array of year/month/day ranges
 */
export async function fetchMemberDateRanges(ecn, meteringPoint, meterCode) {
  if (!ecn) {
    $log.error('No ECN provided. Cannot fetch member date ranges.');
    throw new Error('No ECN provided');
  }
  if (!meteringPoint) {
    $log.error('No metering point provided. Cannot fetch member date ranges.');
    throw new Error('No metering point provided');
  }
  if (!meterCode) {
    $log.error('No meter code provided. Cannot fetch member date ranges.');
    throw new Error('No meter code provided');
  }

  try {
    const url = `${configBackendBaseUrl}data/${ecn}/${meteringPoint}/${meterCode}/dateRange`;
    const response = await axios.get(url);
    return response.data;
  } catch (error) {
    $log.error(`Error fetching member date ranges for ${ecn}/${meteringPoint}/${meterCode}:`, error);
    throw error;
  }
}

/**
 * Get metering point memberships for a community from provided memberships data
 * Filters by active memberships and adds correct meter codes
 * @param {string} ecn - Community ECN
 * @param {Array} allMemberships - All metering point memberships
 * @param {Object} dateRange - Analysis date range {start, end}
 * @returns {Array} Array of filtered metering point membership objects for the community
 */
function _getMeteringPointMembershipsForCommunity(ecn, allMemberships, dateRange = null) {
  if (!allMemberships || !Array.isArray(allMemberships)) {
    $log.warn(`No memberships data provided`);
    return [];
  }

  let memberships = allMemberships.filter(mpm => mpm.ecn === ecn);
  
  // Filter by membership status - only include active or ended memberships
  memberships = memberships.filter(mpm => {
    const status = mpm.membershipStatus;
    return ['ACTIVE', 'ENDED', 'ENDING'].includes(status);
  });
  
  // If date range provided, filter by date overlap
  if (dateRange) {
    memberships = memberships.filter(mpm => {
      // Membership must be active during some part of the analysis period
      const mpStart = mpm.dateStart;
      const mpEnd = mpm.dateEnd || new Date(); // If no end date, assume still active
      
      // Check if membership period overlaps with analysis period
      return mpStart <= dateRange.end && mpEnd >= dateRange.start;
    });
  }
  
  $log.debug(`Found ${memberships.length} active metering point memberships for ECN: ${ecn} (after filtering)`);
  return memberships;
}

/**
 * Perform completeness analysis for bill runs using the proven classic logic
 * @param {Array} communities - Selected communities
 * @param {Object} dateRange - Date range with start and end dates
 * @param {Array} allMemberships - All metering point memberships data (optional)
 * @returns {Promise<Object>} Analysis results
 */
export async function performCompletenessAnalysis(communities, dateRange, allMemberships = []) {
  if (!communities || communities.length === 0) {
    throw new Error('No communities provided for analysis');
  }
  
  if (!dateRange || !dateRange.start || !dateRange.end) {
    throw new Error('Invalid date range provided for analysis');
  }
  
  $log.info(`Starting completeness analysis for ${communities.length} communities`, { 
    communities: communities.map(c => c.ecn), 
    dateRange: `${dateRange.start.toISOString().split('T')[0]} to ${dateRange.end.toISOString().split('T')[0]}` 
  });
  
  const results = {
    communities: [],
    summary: {
      totalCommunities: communities.length,
      communitiesWithData: 0,
      communitiesWithoutData: 0,
      communitiesWithMissingData: 0
    }
  };
  
  for (const community of communities) {
    try {
      $log.info(`Analyzing community: ${community.name} (${community.ecn})`);
      
      // Step 1: Fetch community date ranges and filter by date range (like classic implementation)
      const communityRangesRaw = await fetchCommunityDateRanges(community.ecn);
      const dateRangeCommunity = _reduceDateRangeDataByDateRange(
        communityRangesRaw, 
        new Date(dateRange.start), 
        new Date(dateRange.end)
      );
      const dateRangeCommunityInDates = _convertDateRangeToDates(dateRangeCommunity);
      
      if (dateRangeCommunity.length === 0) {
        $log.warn(`No data found for community ${community.ecn} in specified date range`);
        results.communities.push({
          name: community.name,
          ecn: community.ecn,
          hasData: false,
          expectedDays: 0,
          availableDays: 0,
          missingDays: 0,
          completenessPercentage: 0,
          meteringPointsWithMissingDataCount: 0,
          error: 'No data found in date range'
        });
        results.summary.communitiesWithoutData++;
        continue;
      }
      
      // Step 2: Get memberships for this community (like classic implementation)
      const memberships = _getMeteringPointMembershipsForCommunity(community.ecn, allMemberships, dateRange)
        .map(mpm => {
          // Ensure dates are parsed (like classic implementation)
          let newMpm = { ...mpm };
          if (mpm.dateStart && typeof mpm.dateStart === 'string')
            newMpm.dateStart = new Date(mpm.dateStart);
          if (mpm.dateEnd && typeof mpm.dateEnd === 'string')
            newMpm.dateEnd = new Date(mpm.dateEnd);
          return newMpm;
        });
      
             // Step 3: Fetch member data for each metering point (like classic implementation)
       let memberDateDataDates = {};
       
       for (const membership of memberships) {
         try {
           // Skip non-activated memberships (like classic implementation)
           if (!membership.dateStart) {
             $log.debug(`Skipping non-activated membership: ${membership.meteringPoint}`);
             continue;
           }
           
           // Determine meter code based on direction (like classic implementation)
           const meterCode = membership.meteringPointDirection === 'GENERATION' ? 
             MeterCodes.producerProductionTotal : MeterCodes.memberConsumptionTotal;
           
           const dateDataMember = await fetchMemberDateRanges(
             membership.ecn, 
             membership.meteringPoint, 
             meterCode
           );
           
           const memberDateRange = _reduceDateRangeDataByDateRange(
             dateDataMember,
             new Date(dateRange.start),
             new Date(dateRange.end)
           );
           
           const memberDatesConverted = _convertDateRangeToDates(memberDateRange);
           
           memberDateDataDates[membership.meteringPoint] = memberDatesConverted;
           
           if (memberDatesConverted.length === 0) {
             $log.debug(`No data found for metering point ${membership.meteringPoint} in date range`);
           }
           
         } catch (error) {
           $log.error(`Error fetching data for metering point ${membership.meteringPoint}:`, error);
           // Initialize empty data for this metering point
           memberDateDataDates[membership.meteringPoint] = [];
         }
       }
      
             // Step 4: Check completeness day by day for the FULL QUERY PERIOD (not just community data period)
       let counterDaysComplete = 0;
       let counterDaysIncomplete = 0;
       let meteringPointMissingOnDates = {}; // associative array
       
       // Generate full query period dates
       const queryPeriodDates = [];
       const currentDate = new Date(dateRange.start);
       while (currentDate <= dateRange.end) {
         queryPeriodDates.push(new Date(currentDate));
         currentDate.setDate(currentDate.getDate() + 1);
       }
       
       for (const day of queryPeriodDates) {
         let dataOnDayMissing = false;
         
         // First check if the community has ANY data for this day
         const communityHasDataForDay = dateRangeCommunityInDates.some(communityDay => 
           communityDay.getTime() === day.getTime()
         );
         
         if (!communityHasDataForDay) {
           // Entire community missing data for this day - mark all active metering points as missing
           for (const membership of memberships) {
             // Check if membership should be active on this day
             let notYetActiveOrAlreadyDeactive = false;
             if (membership.dateStart?.getTime() > day.getTime()) {
               notYetActiveOrAlreadyDeactive = true;
             }
             if (membership.dateEnd?.getTime() < day.getTime()) {
               notYetActiveOrAlreadyDeactive = true;
             }
             
             if (!notYetActiveOrAlreadyDeactive) {
               if (!meteringPointMissingOnDates[membership.meteringPoint])
                 meteringPointMissingOnDates[membership.meteringPoint] = [];
               meteringPointMissingOnDates[membership.meteringPoint].push(new Date(day.getTime()));
               dataOnDayMissing = true;
             }
           }
         } else {
           // Community has data for this day, check individual metering points
           for (const meteringPoint in memberDateDataDates) {
             const memberData = memberDateDataDates[meteringPoint];
             const relevantMemberships = memberships.filter(mpm => mpm.meteringPoint === meteringPoint);
             
             // Should there be data for this member(ship)? (like classic implementation)
             let notYetActiveOrAlreadyDeactive = false;
             for (const mpm of relevantMemberships) {
               if (mpm?.dateStart?.getTime() > day.getTime()) {
                 notYetActiveOrAlreadyDeactive = true;
                 break;
               }
               if (mpm?.dateEnd?.getTime() < day.getTime()) {
                 notYetActiveOrAlreadyDeactive = true;
                 break;
               }
             }
             if (notYetActiveOrAlreadyDeactive) {
               continue;
             }
             
             // Check if data is missing for this day
             const hasDataForDay = memberData.find(date => date.getTime() === day.getTime());
             
             if (!hasDataForDay) {
               if (!meteringPointMissingOnDates[meteringPoint])
                 meteringPointMissingOnDates[meteringPoint] = [];
               meteringPointMissingOnDates[meteringPoint].push(new Date(day.getTime()));
               dataOnDayMissing = true;
             }
           }
         }
         
         if (dataOnDayMissing)
           counterDaysIncomplete++;
         else
           counterDaysComplete++;
       }
       

      
      // Step 5: Create detailed result (like classic implementation)
      const dateDataStart = dateRangeCommunityInDates.length > 0 ? dateRangeCommunityInDates[0] : null;
      const dateDataEnd = dateRangeCommunityInDates.length > 0 ? dateRangeCommunityInDates[dateRangeCommunityInDates.length - 1] : null;
      
      // Calculate missing days before and after data range
      const daysCommunityMissingBefore = dateDataStart ? Math.max(0, 
        Math.floor((dateDataStart.getTime() - dateRange.start.getTime()) / (1000 * 60 * 60 * 24))
      ) : Math.floor((dateRange.end.getTime() - dateRange.start.getTime()) / (1000 * 60 * 60 * 24)) + 1;
      
      const daysCommunityMissingAfter = dateDataEnd ? Math.max(0,
        Math.floor((dateRange.end.getTime() - dateDataEnd.getTime()) / (1000 * 60 * 60 * 24))
      ) : 0;
      
       // Create missing date ranges for detailed reporting
       const meteringPointsWithMissingData = [];
       
       for (const meteringPoint in meteringPointMissingOnDates) {
         const missingDates = meteringPointMissingOnDates[meteringPoint];
         const missingRanges = _extractDateRangesFromDateArray(missingDates);
         
         meteringPointsWithMissingData.push({
           meteringPoint: meteringPoint,
           missingDatesCount: missingDates.length,
           missingDates: missingDates,
           missingRanges: missingRanges,
           meteringPointDirection: memberships.find(m => m.meteringPoint === meteringPoint)?.meteringPointDirection
         });
       }
      
      const communityResult = {
        name: community.name,
        ecn: community.ecn,
        expectedDays: queryPeriodDates.length, // Full query period, not just community data period
        availableDays: counterDaysComplete + counterDaysIncomplete,
        completeDays: counterDaysComplete,
        incompleteDays: counterDaysIncomplete,
        missingDays: counterDaysIncomplete,
        completenessPercentage: queryPeriodDates.length > 0 ? 
          (counterDaysComplete / queryPeriodDates.length * 100).toFixed(1) : 0,
        hasData: dateRangeCommunityInDates.length > 0,
        
        // Date range information
        firstDayWithData: dateDataStart,
        lastDayWithData: dateDataEnd,
        daysMissingBefore: daysCommunityMissingBefore,
        daysMissingAfter: daysCommunityMissingAfter,
        
        // Metering point analysis
        totalMeteringPoints: Object.keys(memberDateDataDates).length,
        meteringPointsWithMissingData: meteringPointsWithMissingData,
        meteringPointsWithMissingDataCount: meteringPointsWithMissingData.length,
        
        // Raw data for detailed analysis
        dateRangeCommunityInDates: dateRangeCommunityInDates,
        memberDateDataDates: memberDateDataDates,
        meteringPointMissingOnDates: meteringPointMissingOnDates
      };
      
      results.communities.push(communityResult);
      
      if (communityResult.hasData) {
        results.summary.communitiesWithData++;
      } else {
        results.summary.communitiesWithoutData++;
      }
      
      // Count communities with missing data (at least one metering point with missing data)
      if (communityResult.meteringPointsWithMissingDataCount && communityResult.meteringPointsWithMissingDataCount > 0) {
        results.summary.communitiesWithMissingData++;
      }
      
    } catch (error) {
      $log.error(`Error analyzing community ${community.ecn}:`, error);
      results.communities.push({
        name: community.name,
        ecn: community.ecn,
        error: error.message,
        hasData: false,
        expectedDays: 0,
        availableDays: 0,
        missingDays: 0,
        completenessPercentage: 0,
        meteringPointsWithMissingDataCount: 0
      });
      results.summary.communitiesWithoutData++;
    }
  }
  
  $log.info(`Completeness analysis completed - ${results.summary.communitiesWithData}/${results.summary.totalCommunities} communities with data`);
  return results;
}

/**
 * Fetch individual metering point series data (needed for dynamic distribution analysis)
 * @param {string} ecn - Community ECN
 * @param {string} ecid - Community ECID
 * @param {string} meteringPoint - Metering point identifier
 * @param {string} meterCode - Meter code to fetch
 * @param {Date} dateFrom - Start date
 * @param {Date} dateTo - End date
 * @returns {Promise<Array>} Array of series data
 * @private
 */
async function _fetchDataSeriesSingleMeteringPointMeterCode(ecn, ecid, meteringPoint, meterCode, dateFrom, dateTo) {
  try {
    // Use classic URL pattern: /data/series/{ecn}/{meteringPoint}/{meterCode}/from/{year}/{month}/{day}/to/{year}/{month}/{day}
    const url = `${configBackendBaseUrl}data/series/${ecn}/${meteringPoint}/${encodeURIComponent(meterCode)}/from/${dateFrom.getFullYear()}/${dateFrom.getMonth() + 1}/${dateFrom.getDate()}/to/${dateTo.getFullYear()}/${dateTo.getMonth() + 1}/${dateTo.getDate()}`;
    
    $log.debug("Fetching individual metering point series data:", url);
    const response = await axios.get(url);
    return response.data;
  } catch (error) {
    if (error.response && error.response.status === 404) {
      $log.debug(`No series data found for ${ecn}/${meteringPoint}/${meterCode}, returning empty array`);
      return []; // Return empty array for 404 (no data available)
    }
    $log.error(`Error fetching series data for ${ecn}/${meteringPoint}/${meterCode}:`, error);
    throw error;
  }
}

/**
 * Fetch aggregated series data for a specific meter code from a community (classic implementation)
 * Uses the backend's community-wide aggregation endpoint that handles the logic correctly
 * @param {string} ecn - Community ECN
 * @param {string} meterCode - Meter code to fetch
 * @param {Date} dateFrom - Start date
 * @param {Date} dateTo - End date
 * @param {Array} allMemberships - All metering point memberships (unused, kept for compatibility)
 * @returns {Promise<Array>} Array of aggregated series data
 */
export async function fetchSeriesMeterCodeFromCommunity(ecn, meterCode, dateFrom, dateTo, allMemberships = null) {
  if (!ecn) {
    $log.error('No ECN provided. Cannot fetch series meter code data.');
    throw new Error('No ECN provided');
  }
  if (!meterCode) {
    $log.error('No meter code provided. Cannot fetch series meter code data.');
    throw new Error('No meter code provided');
  }
  if (!dateFrom || !dateTo) {
    $log.error('No date range provided. Cannot fetch series meter code data.');
    throw new Error('No date range provided');
  }

  try {
    // Use the backend's community-wide aggregation endpoint (like classic implementation)
    // /data/series/{ecn}/all/{mc}/from/{yearf}/{monthf}/{dayf}/to/{yeart}/{montht}/{dayt}
    const url = `${configBackendBaseUrl}data/series/${ecn}/all/${encodeURIComponent(meterCode)}/from/${dateFrom.getFullYear()}/${dateFrom.getMonth() + 1}/${dateFrom.getDate()}/to/${dateTo.getFullYear()}/${dateTo.getMonth() + 1}/${dateTo.getDate()}`;
    
    $log.debug("Fetching community aggregated series data:", url);
    const response = await axios.get(url);
    
    $log.debug(`Fetched ${response.data.length} time slots for ${ecn}/${meterCode}`);
    return response.data;

  } catch (error) {
    if (error.response && error.response.status === 404) {
      $log.debug(`No series data found for ${ecn}/${meterCode}, returning empty array`);
      return []; // Return empty array for 404 (no data available)
    }
    $log.error(`Error fetching community aggregated series data for ${ecn}/${meterCode}:`, error);
    throw error;
  }
}

/**
 * Sort series data by date string start (used in sanity check)
 * @param {Object} a - First series entry
 * @param {Object} b - Second series entry
 * @returns {number} Sort comparison result
 * @private
 */
function _sortByDatestringStart(a, b) {
  return new Date(a.datestringStart).getTime() - new Date(b.datestringStart).getTime();
}



/**
 * Perform sanity check analysis for bill runs (energy balance validation)
 * @param {Array} communities - Selected communities
 * @param {Object} dateRange - Date range with start and end dates
 * @param {Array} allMemberships - All metering point memberships (optional, will fetch if not provided)
 * @returns {Promise<Object>} Sanity analysis results
 */
export async function performSanityAnalysis(communities, dateRange, allMemberships = null) {
  if (!communities || communities.length === 0) {
    throw new Error('No communities provided for sanity analysis');
  }
  
  if (!dateRange || !dateRange.start || !dateRange.end) {
    throw new Error('Invalid date range provided for sanity analysis');
  }
  
  $log.info(`Starting sanity analysis for ${communities.length} communities`, { 
    communities: communities.map(c => c.ecn), 
    dateRange: `${dateRange.start.toISOString().split('T')[0]} to ${dateRange.end.toISOString().split('T')[0]}` 
  });
  
  const results = {
    communities: [],
    summary: {
      totalCommunities: communities.length,
      communitiesAnalyzed: 0,
      communitiesWithErrors: 0,
      communitiesWithWarnings: 0
    }
  };
  
  // Important dates for producer share changes (from classic implementation)
  const dateChangeProducerShares = new Date(2022, 10 - 1, 1); // 01.10.2022
  const dateChangeProducerSharesMft = new Date(2024, 4 - 1, 8); // 08.04.2024
  const localeToUse = "de-AT";
  
  for (const community of communities) {
    try {
      $log.info(`Analyzing community for sanity: ${community.name} (${community.ecn})`);
      
      // Warn about not working check before Oct'22 (from classic implementation)
      const warningDueToBeforeOct2022 = (dateRange.start.getTime() - dateChangeProducerShares.getTime()) < 0;
      
      let seriesConsumerShares;
      let seriesProducersTotal;
      let seriesProducersRemainder;
      let seriesProducersTotalForCommunity;
      let seriesProducersRemainderForCommunity;
      
      // Fetch all required series data (from classic implementation)
      try {
        seriesConsumerShares = await fetchSeriesMeterCodeFromCommunity(
          community.ecn,
          MeterCodes.memberShareEffective,
          dateRange.start,
          dateRange.end,
          allMemberships
        );
        seriesConsumerShares.sort(_sortByDatestringStart);
        
        seriesProducersTotal = await fetchSeriesMeterCodeFromCommunity(
          community.ecn,
          MeterCodes.producerProductionTotal,
          dateRange.start,
          dateRange.end,
          allMemberships
        );
        seriesProducersTotal.sort(_sortByDatestringStart);
        
        // Should be present 1.10.2022 to 7.4.2024
        seriesProducersRemainder = await fetchSeriesMeterCodeFromCommunity(
          community.ecn,
          MeterCodes.producerProductionRemainder,
          dateRange.start,
          dateRange.end,
          allMemberships
        );
        seriesProducersRemainder.sort(_sortByDatestringStart);
        
        // Should be present from 8.4.2024
        seriesProducersTotalForCommunity = await fetchSeriesMeterCodeFromCommunity(
          community.ecn,
          MeterCodes.memberProductionForCommunityMft,
          dateRange.start,
          dateRange.end,
          allMemberships
        );
        seriesProducersTotalForCommunity.sort(_sortByDatestringStart);
        
        seriesProducersRemainderForCommunity = await fetchSeriesMeterCodeFromCommunity(
          community.ecn,
          MeterCodes.producerProductionRemainderMft,
          dateRange.start,
          dateRange.end,
          allMemberships
        );
        seriesProducersRemainderForCommunity.sort(_sortByDatestringStart);
        
      } catch (error) {
        $log.warn(`Error during data request for ${community.ecn}:`, error);
        results.communities.push({
          name: community.name,
          ecn: community.ecn,
          error: 'Error during data request',
          hasData: false
        });
        results.summary.communitiesWithErrors++;
        continue;
      }
      
      // Validate data availability (from classic implementation)
      if (seriesConsumerShares.length === 0) {
        results.communities.push({
          name: community.name,
          ecn: community.ecn,
          error: 'No consumer data available, analysis not possible',
          hasData: false
        });
        results.summary.communitiesWithErrors++;
        continue;
      }
      
      if (seriesProducersTotal.length === 0) {
        results.communities.push({
          name: community.name,
          ecn: community.ecn,
          error: 'No producer data available, analysis not possible',
          hasData: false
        });
        results.summary.communitiesWithErrors++;
        continue;
      }
      
      // Check data consistency (from classic implementation)
      // For MFT period (after April 8, 2024), only validate MFT-specific meter codes
      const isMftPeriod = dateRange.start >= dateChangeProducerSharesMft;
      
      let dataConsistencyError = false;
      
      if (isMftPeriod) {
        // MFT period: validate against MFT-specific meter codes only
        if (seriesConsumerShares.length !== seriesProducersTotalForCommunity.length ||
            seriesConsumerShares.length !== seriesProducersRemainderForCommunity.length) {
          dataConsistencyError = true;
        }
      } else {
        // Legacy period: validate against legacy meter codes
        if (seriesConsumerShares.length !== seriesProducersTotal.length ||
            seriesProducersRemainder.length && seriesConsumerShares.length !== seriesProducersRemainder.length) {
          dataConsistencyError = true;
        }
      }
      
      if (dataConsistencyError) {
        // Add debugging information to understand the data inconsistency
        $log.error(`Data inconsistency for ${community.ecn}:`, {
          isMftPeriod,
          seriesConsumerShares: seriesConsumerShares.length,
          seriesProducersTotal: seriesProducersTotal.length,
          seriesProducersRemainder: seriesProducersRemainder.length,
          seriesProducersTotalForCommunity: seriesProducersTotalForCommunity.length,
          seriesProducersRemainderForCommunity: seriesProducersRemainderForCommunity.length
        });
        
        results.communities.push({
          name: community.name,
          ecn: community.ecn,
          error: 'Data inconsistency during data query, contact support',
          hasData: false
        });
        results.summary.communitiesWithErrors++;
        continue;
      }
      
      // Perform energy balance analysis (from classic implementation)
      let index = 0;
      let resultSeriesSum = []; // {dateTime, sumInMinusOut}
      let resultSeriesSumL2 = [];
      let resultSeriesSumL3 = [];
      let resultSeriesSumNotL1 = [];
      
      let totalSumInMinusOut = 0;
      let totalDeviations = 0;
      let totalSumInMinusOutL2 = 0;
      let totalDeviationsL2 = 0;
      let totalSumInMinusOutL3 = 0;
      let totalDeviationsL3 = 0;
      let totalSumInMinusOutNotL1 = 0;
      let totalDeviationsNotL1 = 0;
      
      let daysWithL3 = [];
      let daysWithL2 = [];
      let daysWithNotL1 = [];
      let daysWithNotZero = [];
      
      const thresholdSumNotZero = 0.0011; // From classic implementation
      let totalSumIn = 0;
      let totalSumOut = 0;
      
      // Determine which series to use based on period
      const primaryProducerSeries = isMftPeriod ? seriesProducersTotalForCommunity : seriesProducersTotal;
      const remainderProducerSeries = isMftPeriod ? seriesProducersRemainderForCommunity : seriesProducersRemainder;
      
      // Validate that we have the required series for the period
      if (primaryProducerSeries.length === 0 || remainderProducerSeries.length === 0) {
        const missingSeriesType = isMftPeriod ? 'MFT' : 'legacy';
        results.communities.push({
          name: community.name,
          ecn: community.ecn,
          error: `Missing ${missingSeriesType} producer data for analysis period`,
          hasData: false
        });
        results.summary.communitiesWithErrors++;
        continue;
      }
      
      for (const entry of seriesConsumerShares) {
        let currentDay = new Date(primaryProducerSeries[index].datestringStart);
        currentDay.setHours(0, 0, 0, 0); // local time zone
        
        const isMft = currentDay >= dateChangeProducerSharesMft;
        let sumInMinusOut = 0;
        let notL1present = false;
        let l2present = false;
        let l3present = false;
        
        if (isMft) {
          // MFT period logic (from classic implementation)
          if (new Date(seriesConsumerShares[index].datestringStart).getTime() !==
              new Date(remainderProducerSeries[index].datestringStart).getTime() ||
              new Date(seriesConsumerShares[index].datestringStart).getTime() !==
              new Date(primaryProducerSeries[index].datestringStart).getTime()) {
            throw new Error('Date alignment error in MFT period');
          }
          
          totalSumIn += primaryProducerSeries[index].billingQuantity - 
                       remainderProducerSeries[index].billingQuantity;
          totalSumOut += seriesConsumerShares[index].billingQuantity;
          
          sumInMinusOut = primaryProducerSeries[index].billingQuantity - 
                         remainderProducerSeries[index].billingQuantity -  
                         seriesConsumerShares[index].billingQuantity;
          
          notL1present = primaryProducerSeries[index].meteringMethod !== "L1" ||
                        remainderProducerSeries[index].meteringMethod !== "L1" ||
                        seriesConsumerShares[index].meteringMethod !== "L1";
          
          l2present = primaryProducerSeries[index].meteringMethod === "L2" ||
                     remainderProducerSeries[index].meteringMethod === "L2" ||
                     seriesConsumerShares[index].meteringMethod === "L2";
          
          l3present = primaryProducerSeries[index].meteringMethod === "L3" ||
                     remainderProducerSeries[index].meteringMethod === "L3" ||
                     seriesConsumerShares[index].meteringMethod === "L3";
                     
        } else {
          // Non-MFT period logic (from classic implementation)
          if (remainderProducerSeries.length === 0) {
            throw new Error('Missing producer remainder data for non-MFT period');
          }
          
          if (new Date(primaryProducerSeries[index].datestringStart).getTime() !==
              new Date(remainderProducerSeries[index].datestringStart).getTime() ||
              new Date(primaryProducerSeries[index].datestringStart).getTime() !==
              new Date(seriesConsumerShares[index].datestringStart).getTime()) {
            throw new Error('Date alignment error in non-MFT period');
          }
          
          totalSumIn += primaryProducerSeries[index].billingQuantity - 
                       remainderProducerSeries[index].billingQuantity;
          totalSumOut += seriesConsumerShares[index].billingQuantity;
          
          sumInMinusOut = primaryProducerSeries[index].billingQuantity - 
                         remainderProducerSeries[index].billingQuantity -  
                         seriesConsumerShares[index].billingQuantity;
          
          notL1present = primaryProducerSeries[index].meteringMethod !== "L1" ||
                        remainderProducerSeries[index].meteringMethod !== "L1" ||
                        seriesConsumerShares[index].meteringMethod !== "L1";
          
          l2present = primaryProducerSeries[index].meteringMethod === "L2" ||
                     remainderProducerSeries[index].meteringMethod === "L2" ||
                     seriesConsumerShares[index].meteringMethod === "L2";
          
          l3present = primaryProducerSeries[index].meteringMethod === "L3" ||
                     remainderProducerSeries[index].meteringMethod === "L3" ||
                     seriesConsumerShares[index].meteringMethod === "L3";
        }
        
        // Track days with sum not equal zero
        if (Math.abs(sumInMinusOut) > thresholdSumNotZero) {
          if (daysWithNotZero.length === 0 ||
              daysWithNotZero[daysWithNotZero.length - 1].getDate() !== currentDay.getDate()) {
            daysWithNotZero.push(new Date(currentDay));
          }
        }
        
        // Categorize by metering method (from classic implementation)
        if (notL1present) {
          if (l3present) {
            resultSeriesSumL3.push({
              dateTime: new Date(primaryProducerSeries[index].datestringStart),
              sumInMinusOut: sumInMinusOut,
            });
            
            totalSumInMinusOutL3 += sumInMinusOut;
            totalDeviationsL3 += Math.abs(sumInMinusOut);
            
            if (daysWithL3.length === 0 ||
                daysWithL3[daysWithL3.length - 1].getDate() !== currentDay.getDate()) {
              daysWithL3.push(new Date(currentDay));
            }
          } else if (l2present) {
            resultSeriesSumL2.push({
              dateTime: new Date(primaryProducerSeries[index].datestringStart),
              sumInMinusOut: sumInMinusOut,
            });
            
            totalSumInMinusOutL2 += sumInMinusOut;
            totalDeviationsL2 += Math.abs(sumInMinusOut);
            
            if (daysWithL2.length === 0 ||
                daysWithL2[daysWithL2.length - 1].getDate() !== currentDay.getDate()) {
              daysWithL2.push(new Date(currentDay));
            }
          } else { // something else (and not L2 or L3) - includes LX
            resultSeriesSumNotL1.push({
              dateTime: new Date(primaryProducerSeries[index].datestringStart),
              sumInMinusOut: sumInMinusOut,
            });
            
            totalSumInMinusOutNotL1 += sumInMinusOut;
            totalDeviationsNotL1 += Math.abs(sumInMinusOut);
            
            if (daysWithNotL1.length === 0 ||
                daysWithNotL1[daysWithNotL1.length - 1].getDate() !== currentDay.getDate()) {
              daysWithNotL1.push(new Date(currentDay));
            }
          }
        } else {
          resultSeriesSum.push({
            dateTime: new Date(primaryProducerSeries[index].datestringStart),
            sumInMinusOut: sumInMinusOut,
          });
          
          totalSumInMinusOut += sumInMinusOut;
          totalDeviations += Math.abs(sumInMinusOut);
        }
        
        index++;
      }
      
      // Create community result
      const communityResult = {
        name: community.name,
        ecn: community.ecn,
        hasData: true,
        warningDueToBeforeOct2022: warningDueToBeforeOct2022,
        
        // Energy sums
        totalSumIn: totalSumIn,
        totalSumOut: totalSumOut,
        
        // L1 (measured) values
        countL1QuarterHours: resultSeriesSum.length,
        totalDeviationL1: totalSumInMinusOut,
        totalAbsoluteDeviationL1: totalDeviations,
        
        // L2 (reliable substitute) values  
        countL2QuarterHours: resultSeriesSumL2.length,
        totalDeviationL2: totalSumInMinusOutL2,
        totalAbsoluteDeviationL2: totalDeviationsL2,
        daysWithL2: daysWithL2,
        
        // L3 (unreliable substitute) values
        countL3QuarterHours: resultSeriesSumL3.length,
        totalDeviationL3: totalSumInMinusOutL3,
        totalAbsoluteDeviationL3: totalDeviationsL3,
        daysWithL3: daysWithL3,
        
        // Unknown classification (includes LX and others)
        countNotL1QuarterHours: resultSeriesSumNotL1.length,
        totalDeviationNotL1: totalSumInMinusOutNotL1,
        totalAbsoluteDeviationNotL1: totalDeviationsNotL1,
        daysWithNotL1: daysWithNotL1,
        
        // Days with deviations above threshold
        daysWithNotZero: daysWithNotZero,
        thresholdSumNotZero: thresholdSumNotZero,
        
        // Locale for formatting
        localeToUse: localeToUse
      };
      
      results.communities.push(communityResult);
      results.summary.communitiesAnalyzed++;
      
      // Check for errors and warnings - L3 and unknown classifications are errors, L2 and deviations are warnings
      let hasErrors = false;
      let hasWarnings = false;
      
      // L3 and unknown metering methods (including LX) are considered errors
      if (daysWithL3.length > 0 || daysWithNotL1.length > 0) {
        results.summary.communitiesWithErrors++;
        hasErrors = true;
      }
      
      // L2 metering method, pre-Oct2022 periods, and balance deviations are warnings (only if no errors)
      if (!hasErrors && (warningDueToBeforeOct2022 || daysWithL2.length > 0 || daysWithNotZero.length > 0)) {
        results.summary.communitiesWithWarnings++;
        hasWarnings = true;
      }
      
    } catch (error) {
      $log.error(`Error analyzing community ${community.ecn}:`, error);
      results.communities.push({
        name: community.name,
        ecn: community.ecn,
        error: error.message,
        hasData: false
      });
      results.summary.communitiesWithErrors++;
    }
  }
  
  $log.info(`Sanity analysis completed - ${results.summary.communitiesAnalyzed}/${results.summary.totalCommunities} communities analyzed`);
  return results;
}

/**
 * Perform dynamic distribution analysis for bill runs
 * Based on the classic quickCheckDynamicDistribution implementation
 * @param {Array} communities - Selected communities
 * @param {Object} dateRange - Date range with start and end dates
 * @param {Array} allMemberships - All metering point memberships data
 * @returns {Promise<Object>} Dynamic distribution analysis results
 */
export async function performDynamicDistributionAnalysis(communities, dateRange, allMemberships = []) {
  if (!communities || communities.length === 0) {
    throw new Error('No communities provided for dynamic distribution analysis');
  }
  
  if (!dateRange || !dateRange.start || !dateRange.end) {
    throw new Error('Invalid date range provided for dynamic distribution analysis');
  }
  
  $log.info(`Starting dynamic distribution analysis for ${communities.length} communities`, { 
    communities: communities.map(c => c.ecn), 
    dateRange: `${dateRange.start.toISOString().split('T')[0]} to ${dateRange.end.toISOString().split('T')[0]}` 
  });
  
  // Import the existing classes
  const EnergyDataSeriesSanitizer = (await import('@/lib/es-bill-shared-js/EnergyDataSeriesSanitizer.js')).default;
  const EnergyDistributionCalculator = (await import('@/lib/es-bill-shared-js/EnergyDistributionCalculator.js')).default;
  
  /**
   * Energy data requester implementation for dynamic distribution analysis
   * Bridges the gap between the bill run service and the energy data sanitizer
   */
  class BillRunServiceEnergyDataRequester {
    constructor(logger) {
      this.$log = logger;
    }

    async fetchDataSeriesSingleMeteringPointMeterCode(ecn, ecid, meteringPoint, meterCode, dateStart, dateEnd) {
      return await _fetchDataSeriesSingleMeteringPointMeterCode(ecn, ecid, meteringPoint, meterCode, dateStart, dateEnd);
    }

    async fetchMeteringPointStats(ecn, ecid, meteringPoint, meterCodes) {
      // Implementation would go here if needed
      return {};
    }
  }
  
  const results = {
    communities: [],
    summary: {
      totalCommunities: communities.length,
      communitiesAnalyzed: 0,
      communitiesWithErrors: 0,
      communitiesWithProblems: 0
    }
  };
  
  for (const community of communities) {
    try {
      $log.info(`Analyzing community for dynamic distribution: ${community.name} (${community.ecn})`);
      
      // Get memberships for this community 
      const memberships = _getMeteringPointMembershipsForCommunity(community.ecn, allMemberships, dateRange)
        .map(mpm => {
          // Ensure dates are parsed
          let newMpm = { ...mpm };
          if (mpm.dateStart && typeof mpm.dateStart === 'string')
            newMpm.dateStart = new Date(mpm.dateStart);
          if (mpm.dateEnd && typeof mpm.dateEnd === 'string')
            newMpm.dateEnd = new Date(mpm.dateEnd);
          return newMpm;
        });
        
      if (memberships.length === 0) {
        $log.warn(`No active memberships found for community ${community.ecn} in date range`);
        results.communities.push({
          name: community.name,
          ecn: community.ecn,
          hasData: false,
          error: 'No active memberships found in date range'
        });
        results.summary.communitiesWithErrors++;
        continue;
      }
      
      // Initialize energy data requester and sanitizer
      const energyDataRequester = new BillRunServiceEnergyDataRequester($log);
      const edt = new EnergyDataSeriesSanitizer(energyDataRequester, $log);
      
      let meteringPointConsidered = [];
      let mpmEnergyData = [];
      let mpmsActiveAll = memberships; // All active memberships for this community
      
      // Process memberships and get energy data
      for (const mpm of memberships) {
        // Skip invalid memberships
        if (mpm.membershipStatus !== "ACTIVE" &&
            mpm.membershipStatus !== "ENDING" &&
            mpm.membershipStatus !== "ENDED") {
          continue;
        }
        
        if (meteringPointConsidered.includes(mpm.meteringPoint)) {
          continue;
        }
        meteringPointConsidered.push(mpm.meteringPoint);
        
        try {
          // Get sanitized energy data for the metering point
          const sumForMpSanitized = await edt.getSeriesMeteringPointUser(
            mpm, 
            new Date(dateRange.start), 
            new Date(dateRange.end)
          );
          
          // Debug the sanitized data
          $log.debug(`Dynamic distribution data for ${mpm.meteringPoint}:`, {
            meteringPoint: mpm.meteringPoint,
            direction: mpm.meteringPointDirection,
            hasSeriesTotal: !!sumForMpSanitized.seriesTotal,
            hasSeriesCommunity: !!sumForMpSanitized.seriesCommunity,
            hasSeriesRemainder: !!sumForMpSanitized.seriesRemainder,
            seriesTotalLength: sumForMpSanitized.seriesTotal?.length || 0,
            seriesCommunityLength: sumForMpSanitized.seriesCommunity?.length || 0,
            seriesRemainderLength: sumForMpSanitized.seriesRemainder?.length || 0
          });
          
          mpmEnergyData.push({
            membership: sumForMpSanitized.membership || mpm,
            energyDataDso: sumForMpSanitized,
            seriesCommunityCalculated: [],
            seriesCommunityDifference: [],
          });
          
        } catch (error) {
          $log.error(`Error getting energy data for metering point ${mpm.meteringPoint}:`, error);
          // Continue with other metering points
        }
      }
      
      if (mpmEnergyData.length === 0) {
        results.communities.push({
          name: community.name,
          ecn: community.ecn,
          hasData: false,
          error: 'No energy data available for any metering points'
        });
        results.summary.communitiesWithErrors++;
        continue;
      }
      
      // Calculate reference distribution using the existing calculator
      const edc = new EnergyDistributionCalculator($log);
      const distributionResult = edc.calculateReferenceDistribution(mpmEnergyData, mpmsActiveAll);
      
      if (distributionResult === false) {
        results.communities.push({
          name: community.name,
          ecn: community.ecn,
          hasData: false,
          error: 'Error during distribution calculation'
        });
        results.summary.communitiesWithErrors++;
        continue;
      }
      
      // Create community result
      const communityResult = {
        name: community.name,
        ecn: community.ecn,
        hasData: true,
        
        // Core distribution results
        distributionResult: distributionResult,
        
        // Summary information
        totalMeteringPoints: meteringPointConsidered.length,
        activeMemberships: mpmsActiveAll.length,
        
        // Problem detection
        hasProblems: distributionResult.missingDistribution && 
          (distributionResult.missingDistribution.communityProblematicDates.length > 0 ||
           distributionResult.missingDistribution.mpsProblematicDates.length > 0),
           
        // Analysis metadata
        analysisVersion: "0.4 (Vue3 Integration)"
      };
      
      results.communities.push(communityResult);
      results.summary.communitiesAnalyzed++;
      
      if (communityResult.hasProblems) {
        results.summary.communitiesWithProblems++;
      }
      
    } catch (error) {
      $log.error(`Error analyzing community ${community.ecn} for dynamic distribution:`, error);
      results.communities.push({
        name: community.name,
        ecn: community.ecn,
        error: error.message,
        hasData: false
      });
      results.summary.communitiesWithErrors++;
    }
  }
  
  $log.info(`Dynamic distribution analysis completed - ${results.summary.communitiesAnalyzed}/${results.summary.totalCommunities} communities analyzed`);
  return results;
} 